class StickyBuyButton extends HTMLElement {
  constructor() {
    super();

    this.activeClass = "wt-product__sticky-buy--show";
  }

  connectedCallback() {
    this.initialize();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  initialize() {
    if (this.dataset.stickyMode === "always") {
      this.initializeAlwaysVisible();
      return;
    }

    const heroSection = this.closest(".wt-product__wrapper");
    const addToCartModule =
      heroSection?.querySelector(
        ".main-product__buy-buttons--container > product-form.wt-product__add-to-cart",
      ) ||
      heroSection?.querySelector(".wt-product__add-to-cart") ||
      document.querySelector(".wt-product__add-to-cart");
    const pageHeroCandidate = document.querySelector(
      "main .section-inverted-libido-hero, main .section-split-hero-banner, main .section-image-with-text",
    );
    const pageHeroPrecedesProduct =
      pageHeroCandidate &&
      heroSection &&
      Boolean(
        pageHeroCandidate.compareDocumentPosition(heroSection) &
          Node.DOCUMENT_POSITION_FOLLOWING,
      );
    const isPageTemplate = Array.from(document.body.classList).some(
      (className) =>
        className === "template-page" ||
        className.startsWith("template-page-"),
    );
    const pageHero =
      isPageTemplate && pageHeroPrecedesProduct
        ? pageHeroCandidate
        : null;
    const heroBoundary = pageHero || addToCartModule || heroSection;
    const footer = document.querySelector(".wt-footer");
    const btn = this.querySelector("button");
    const addToCart = this.dataset.addToCart === "";

    if (!btn || !addToCartModule || !heroBoundary) {
      return;
    }

    btn.addEventListener("click", (e) => {
      if (!addToCart) {
        e.preventDefault();
      }
      addToCartModule.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });

    let hasPassedHero = false;
    let footerIsVisible = false;
    let mainAddToCartIsVisible = false;

    const updateVisibility = () => {
      if (
        hasPassedHero &&
        !mainAddToCartIsVisible &&
        !footerIsVisible
      ) {
        this.classList.add(this.activeClass);
      } else {
        this.classList.remove(this.activeClass);
      }
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === heroBoundary) {
          hasPassedHero =
            !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        }

        if (entry.target === footer) {
          footerIsVisible = entry.isIntersecting;
        }

        if (entry.target === addToCartModule) {
          mainAddToCartIsVisible = entry.isIntersecting;
        }
      });

      updateVisibility();
    });

    if (heroBoundary) {
      this.observer.observe(heroBoundary);
    }

    if (footer) {
      this.observer.observe(footer);
    }

    if (addToCartModule !== heroBoundary) {
      this.observer.observe(addToCartModule);
    }

    updateVisibility();
  }

  // Inverted landing pages: the bar shows everywhere except while the main
  // add-to-cart button, a hero CTA or the footer is on screen, and the button
  // scrolls to the buy box instead of adding to cart directly.
  initializeAlwaysVisible() {
    const heroSection = this.closest(".wt-product__wrapper");
    const buyBox =
      heroSection?.querySelector(".main-product__buy-buttons--container") ||
      heroSection?.querySelector(".wt-product__add-to-cart") ||
      document.querySelector(".main-product__buy-buttons--container");
    const mainButton =
      buyBox?.querySelector('product-form [type="submit"]') || buyBox;
    const footer = document.querySelector(".wt-footer");
    const heroButtons = document.querySelectorAll(".ioh__button--primary");
    const btn = this.querySelector("button");

    if (!btn || !buyBox) return;

    btn.addEventListener("click", (e) => {
      e.preventDefault();
      buyBox.scrollIntoView({ behavior: "smooth", block: "start" });
    });

    let buyBoxIsVisible = false;
    let footerIsVisible = false;
    const visibleHeroButtons = new Set();

    const updateVisibility = () => {
      this.classList.toggle(
        this.activeClass,
        !buyBoxIsVisible && !footerIsVisible && visibleHeroButtons.size === 0,
      );
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.target === mainButton) buyBoxIsVisible = entry.isIntersecting;
        if (entry.target === footer) footerIsVisible = entry.isIntersecting;
        if (entry.target.classList.contains("ioh__button--primary")) {
          if (entry.isIntersecting) visibleHeroButtons.add(entry.target);
          else visibleHeroButtons.delete(entry.target);
        }
      });
      updateVisibility();
    });

    this.observer.observe(mainButton);
    if (footer) this.observer.observe(footer);
    heroButtons.forEach((heroButton) => this.observer.observe(heroButton));
    updateVisibility();
  }
}

customElements.define("sticky-buy-button", StickyBuyButton);

if (!customElements.get("gallery-fashion")) {
  customElements.define(
    "gallery-fashion",
    class GalleryFashion extends HTMLElement {
      constructor() {
        super();
        this.section = this.closest("section");
        this.logoBanner = this;
      }

      connectedCallback() {
        this.init();
      }

      disconnectedCallback() {
        this.removeEventsWhenDesignMode();
      }

      addEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.addEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.addEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      removeEventsWhenDesignMode() {
        if (Shopify.designMode) {
          document.removeEventListener(
            "shopify:section:load",
            this.reinitAfterDelay,
          );
          document.removeEventListener(
            "shopify:section:unload",
            this.reinitAfterDelay,
          );
        }
      }

      reinitAfterDelay() {
        setTimeout(() => this.reinit(), 0);
      }

      isFirstSection() {
        const sectionWrapper = document.querySelector("#root");
        const firstSection = sectionWrapper.querySelector("section");
        this.section = this.closest("section");
        const currentSection = this.section;

        return firstSection === currentSection;
      }

      handleResize() {
        this.setTopMargin();
        this.positioningProductInfo();
      }

      observeHeader() {
        const header = document.querySelector(".wt-header");
        const activeTransparentClass = "wt-header--fashion-transparent";

        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) {
                header.classList.remove(activeTransparentClass);
              } else {
                header.classList.add(activeTransparentClass);
              }
            });
          },
          { root: null, threshold: 0.05 },
        );

        observer.observe(this.querySelector(".wt-product__gallery"));
      }

      calculateOffset() {
        const header = document.querySelector("header");
        const headerHeight = header.offsetHeight;
        const offset =
          this.isTransparentHeaderEnabled() && this.isFirstSection()
            ? headerHeight
            : 0;

        return offset;
      }

      setTopMargin() {
        const offset = this.calculateOffset();
        if (offset) {
          this.section.style.marginTop = `-${offset}px`;
        } else {
          this.section.style.marginTop = 0;
        }
      }

      isTransparentHeaderEnabled() {
        const header = document.querySelector(".wt-header");
        return (
          header.dataset.transparent &&
          header.classList.contains("wt-header--v3")
        );
      }

      renderProgressBar() {
        const thumbsGallery = this.querySelector("[data-thumbs]");
        const progressBarElement = document.createElement("div");
        progressBarElement.classList.add("gallery-fashion__progress-bar");
        const progressBarIndicatorElement = document.createElement("div");
        progressBarIndicatorElement.classList.add(
          "gallery-fashion__progress-bar-indicator",
        );
        progressBarElement.appendChild(progressBarIndicatorElement);
        thumbsGallery.appendChild(progressBarElement);

        function updateProgressBar() {
          const images = this.querySelector("gallery-section");
          const progressBar = progressBarIndicatorElement;

          const scrolled = window.scrollY;
          const maxHeight = images.clientHeight - window.innerHeight;
          const scrollPercentage = (scrolled / maxHeight) * 100;

          const progressBarHeight = progressBarElement.clientHeight;
          const progressHeight = progressBar.clientHeight;
          const maxProgressTop = progressBarHeight - progressHeight;
          const progressTop = Math.min(
            (scrollPercentage / 100) * maxProgressTop,
            maxProgressTop,
          );

          progressBar.style.top = `${progressTop}px`;

          if (progressTop === maxProgressTop) {
            thumbsGallery.classList.add("finished");
          } else {
            thumbsGallery.classList.remove("finished");
          }
        }

        window.addEventListener("scroll", updateProgressBar.bind(this));

        function handleThumbsClick(event) {
          if (event.target.closest(".thumbs-list-item")) {
            const thumbnails = Array.from(
              event.currentTarget.querySelectorAll(".wt-product__img"),
            );
            const targetImage = event.target
              .closest(".thumbs-list-item")
              .querySelector(".wt-product__img");
            const index = thumbnails.indexOf(targetImage);
            const fullImage = this.querySelectorAll(
              ".wt-masonry__wrapper .wt-product__img, .wt-masonry__wrapper .wt-product__thumbnail-video",
            )[index];

            fullImage.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }

        thumbsGallery.addEventListener("click", handleThumbsClick.bind(this));
      }

      positioningProductInfo() {
        const productInfo = this.querySelector(".wt-product__main");
        const mediaDesktop = window.matchMedia("(min-width: 1200px)");

        if (mediaDesktop.matches) {
          productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
        }

        const resizeObserver = new ResizeObserver((entries) => {
          for (let entry of entries) {
            if (mediaDesktop.matches) {
              productInfo.style.top = `${Math.max((window.innerHeight - productInfo.offsetHeight) / 2, 0)}px`;
            }
          }
        });

        const observedElement = productInfo;
        resizeObserver.observe(observedElement);
      }

      attachEvents() {
        window.addEventListener("resize", this.handleResize.bind(this));
      }

      /**
       * Wait until all images inside masonry wrapper are loaded
       * then run callback
       */
      waitForMasonryImages(callback) {
        const masonry = this.querySelector(".swiper-wrapper--masonry");
        if (!masonry) return;

        const images = masonry.querySelectorAll("img");
        if (images.length === 0) {
          callback();
          return;
        }

        let loaded = 0;
        const checkDone = () => {
          loaded++;
          if (loaded === images.length) {
            callback();
          }
        };

        images.forEach((img) => {
          if (img.complete) {
            checkDone();
          } else {
            img.addEventListener("load", checkDone, { once: true });
            img.addEventListener("error", checkDone, { once: true });
          }
        });
      }

      init() {
        this.reinitAfterDelay = this.reinitAfterDelay.bind(this);
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            this.waitForMasonryImages(() => {
              const masonry = this.querySelector(".swiper-wrapper--masonry");
              if (masonry) {
                stickyHeaderThreshold.style.height = `${masonry.offsetHeight}px`;
              }
            });
          }
          this.setTopMargin();
          this.observeHeader();
          this.attachEvents();
        }

        this.renderProgressBar();
        this.positioningProductInfo();
        this.addEventsWhenDesignMode();
      }

      reinit() {
        if (this.isTransparentHeaderEnabled()) {
          const stickyHeaderThreshold = document.querySelector(
            ".sticky-header__threshold",
          );
          const isHeaderSticky =
            document.body.classList.contains("page-header-sticky");
          if (isHeaderSticky) {
            stickyHeaderThreshold.style.height = `${this.querySelector(".swiper-wrapper--masonry").offsetHeight}px`;
          }
          this.setTopMargin();
          this.observeHeader();
        }
        this.positioningProductInfo();
      }
    },
  );
}
